/**
 * The cell codec for painted maps (TrackPaint). Each layer is one character
 * per cell, run-length encoded as `<count><char>` with the count left out for
 * a single cell: "12#3W." is twelve floor cells, three wall cells, one empty.
 * Shared by the validator and the editor so a file means the same thing to both.
 */

/** One character per surface kind, in kind-index order (see src/editor/paint.ts for names and colours). */
export const PAINT_KIND_CODES = '.#W/LSFCGPBKU';
/** Floor heights 0 … 30 m, one character each. */
export const PAINT_HEIGHT_CODES = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcde';

/** Run-length encodes cell values (each an index into `alphabet`). */
export function encodeRuns(values: ArrayLike<number>, alphabet: string): string {
  let out = '';
  for (let i = 0; i < values.length; ) {
    const v = values[i]!;
    let j = i + 1;
    while (j < values.length && values[j] === v) j++;
    const n = j - i;
    out += (n > 1 ? String(n) : '') + alphabet[v]!;
    i = j;
  }
  return out;
}

/**
 * Decodes a run-length layer of exactly `length` cells. Returns an error
 * message for anything else: unknown characters, a zero or oversized count,
 * or too few or too many cells (checked before allocating anything big).
 */
export function decodeRuns(text: string, alphabet: string, length: number): Uint8Array | string {
  if (typeof text !== 'string') return 'must be a string';
  if (text.length > length * 6) return 'is longer than its cells need';
  const out = new Uint8Array(length);
  let at = 0;
  let count = '';
  for (const ch of text) {
    if (ch >= '0' && ch <= '9') {
      if (count.length >= 6) return 'has a run count that is too long';
      count += ch;
      continue;
    }
    const v = alphabet.indexOf(ch);
    if (v < 0) return `has an unknown cell code "${ch}"`;
    const n = count ? Number(count) : 1;
    count = '';
    if (n < 1) return 'has a run of zero cells';
    if (at + n > length) return `has more than ${length} cells`;
    out.fill(v, at, at + n);
    at += n;
  }
  if (count) return 'ends with a count and no cell code';
  if (at !== length) return `has ${at} cells, expected ${length}`;
  return out;
}
